import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { useModal } from 'context/ModalContext';
import { Noke } from 'utilities/iconsNew';
import { userSignOut } from 'actions/Auth';
import { setIsNewUI, setNotificationsCount } from 'actions/App';
import { fetchUserNotificationsCompany } from 'actions/Notifications';
import UserInitialsAvatar from 'components/UserInitialsAvatar';
import Button from 'components/ButtonIconOnly';
import IconWithBackground from 'components/IconWithBackground';
import Icon from 'components/Icon';
import Popout from 'components/Popout';
import Tooltip from 'components/Tooltip';
import NotificationModal from 'components/NotificationModal';
import LanguageSwitcher from 'components/LanguageSwitcher/indexNew';
import UnitsUsersDetailModal from 'app/routes/Sites/routes/Display/UnitsUsers/UnitsUsersDetailModal';

const Sidebar = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const { openModal } = useModal();
    const buttonRef = useRef(null);
    const helpPopoutAnchorRef = useRef(null);
    const userPopoutAnchorRef = useRef(null);

    const overviewRef = useRef(null);
    const unitsUsersRef = useRef(null);
    const moveOutPredictionsRef = useRef(null);
    const activityRef = useRef(null);
    const unitsRef = useRef(null);
    const usersRef = useRef(null);
    const entriesRef = useRef(null);
    const accessCodesRef = useRef(null);
    const networkRef = useRef(null);
    const settingsRef = useRef(null);
    const hoursRef = useRef(null);
    const mapsRef = useRef(null);
    const helpRef = useRef(null);
    const fleetMgmtRef = useRef(null);
    const supportFunctionsRef = useRef(null);

    const authUser = useSelector((state) => state.auth.authUser);
    const myFacility = useSelector((state) => state.reports.myFacility);
    const site = useSelector((state) => state.auth.site);
    const theme = useSelector((state) => state.app.theme);
    const notificationsCount = useSelector((state) => state.app.notificationsCount);
    const notificationsCompany = useSelector((state) => state.notifications.userNotificationsCompany);
    const [isHelpPopoutOpen, setIsHelpPopoutOpen] = useState(false);
    const [isUserPopoutOpen, setIsUserPopoutOpen] = useState(false);
    const [showGeneralHelpMenu, setShowGeneralHelpMenu] = useState(false);
    const [filteredSites] = useState(authUser?.sites);
    const [count, setCount] = useState(0);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0, arrowOffset: 0 });

    const [visible, setVisible] = useState(false);
    const [placement, setPlacement] = useState('bottom');
    const [firstLoad, setFirstLoad] = useState(true);

    const isFacilityView =
        location.pathname !== '/app/facilities/allFacilities' && location.pathname !== '/app/facilities/myFacilities';

    const path = location.pathname.replace(/\/$/, '');
    const segments = path.split('/');
    const getActiveRouteKey = () => {
        const facilityIndex = segments.indexOf('facility');
        if (facilityIndex !== -1 && segments.length > facilityIndex + 2) {
            return segments[facilityIndex + 2]; // e.g., 'overview', 'activity'
        }

        // Old UI: match known patterns
        if (path.includes('app/user/facilities/all')) return 'user/facilities';
        if (path.includes('app/users/view')) return 'view';

        // Fallback: use first or second segment
        const first = segments[1] || '';
        const second = segments[2] || '';
        return second || first;
    };

    const activeRouteKey = getActiveRouteKey();
    const isNewUIActive = (item) => activeRouteKey === item.name;
    const isOldUIActive = (item) => {
        if (activeRouteKey === 'unitsusers') return false; // need this here because unitsusers includes both units and users ha ha ha
        return (
            activeRouteKey === item.name ||
            activeRouteKey === item.reference ||
            path.includes(item.name) ||
            path.includes(item.reference)
        );
    };

    const facilityMenu = [
        { name: 'overview', icon: <Icon name="dashboard" size={20} />, ref: overviewRef, tooltipText: 'Overview' },
        { name: 'unitsusers', icon: <Icon name="users" size={20} />, ref: unitsUsersRef, tooltipText: 'Units & Users' },
        { name: 'activity', icon: <Icon name="activity" size={20} />, ref: activityRef, tooltipText: 'Activity' },
        {
            name: 'reports',
            icon: <Icon name="reports" size={20} />,
            ref: moveOutPredictionsRef,
            tooltipText: 'Reports'
        },
        { name: 'settings', icon: <Icon name="settings" size={20} />, ref: settingsRef, tooltipText: 'Settings' }
    ];

    const oldUIMenu = [
        {
            id: 'pages.units.TYPES',
            ref: unitsRef,
            name: 'units/all',
            reference: 'units',
            icon: 'storageUnit',
            permission: ['view_site_locks'],
            subFeature: 'new_ui_v3',
            tooltipText: 'pages.units.Types'
        },
        {
            id: 'pages.users.TYPES',
            ref: usersRef,
            name: 'users/all',
            reference: 'users',
            icon: 'user',
            permission: ['manage_users'],
            subFeature: 'new_ui_v3',
            tooltipText: 'pages.users.Types'
        },
        {
            id: 'pages.entry.TYPES',
            ref: entriesRef,
            name: 'entry/all',
            reference: 'entry',
            icon: 'closedDoor',
            permission: ['view_entries'],
            subFeature: 'new_ui_v3',
            tooltipText: 'pages.entry.Types'
        },
        {
            id: 'pages.accessCodes.TYPES',
            ref: accessCodesRef,
            name: 'accessCodes/all',
            reference: 'accessCodes',
            icon: 'accessCodes',
            permission: ['support_get_access_codes'],
            keypad: true,
            tooltipText: 'pages.accessCodes.Types'
        },
        {
            id: 'pages.network.TYPES',
            ref: networkRef,
            name: 'network/all',
            reference: 'network',
            icon: 'network',
            permission: ['manage_gateways'],
            tooltipText: 'pages.network.TYPES'
        },
        {
            id: 'pages.settings.TYPES',
            ref: settingsRef,
            name: 'settings',
            reference: 'settings',
            icon: 'settings',
            tooltipText: 'pages.settings.Types'
        },
        {
            id: 'pages.hours.Type',
            ref: hoursRef,
            name: 'hours',
            reference: 'hours',
            icon: 'hours',
            permission: ['manage_sites'],
            tooltipText: 'pages.hours.Type'
        },
        {
            id: 'pages.maps.TYPES',
            ref: mapsRef,
            name: 'maps/all',
            reference: 'maps',
            icon: 'map',
            permission: ['view_site_maps'],
            feature: 'site_map',
            tooltipText: 'pages.maps.Types'
        }
    ];

    if (!authUser.featureflagAssignments?.includes('new_ui_v3')) {
        const fleetMgmtOption = {
            id: 'pages.fleetmgmt.TYPES',
            ref: fleetMgmtRef,
            name: 'fleetmgmt',
            reference: 'fleetmgmt',
            icon: 'fleetMgmt',
            permission: [
                'digital_audit_view',
                'digital_audit_submitter',
                'digital_audit_initial_approver',
                'digital_audit_final_approver'
            ],
            tooltipText: 'pages.fleetmgmt.TYPES'
        };

        const reportsOptions = {
            id: 'pages.reports.TYPES',
            ref: moveOutPredictionsRef,
            name: 'reports',
            reference: 'reports',
            icon: 'reports',
            tooltipText: 'pages.reports.Types'
        };

        const supportFunctionsOption = {
            id: 'pages.supportFunctions.TYPES',
            ref: supportFunctionsRef,
            name: 'supportFunctions',
            reference: 'supportFunctions',
            icon: 'support',
            permission: [
                'support_view_users',
                'support_view_all_companies',
                'support_manage_support',
                'support_daily_digest',
                'support_manage_featureflags',
                'support_view_site_notes'
            ],
            tooltipText: 'pages.supportFunctions.Types'
        };

        const settingsIndex = oldUIMenu.findIndex((item) => item.name === 'settings');

        if (settingsIndex !== -1) {
            oldUIMenu.splice(settingsIndex, 0, fleetMgmtOption);
            oldUIMenu.splice(settingsIndex + 1, 0, reportsOptions);
            oldUIMenu.splice(settingsIndex + 2, 0, supportFunctionsOption);
        }

        // remove reports from the facility menu since it's being moved to the old UI section of the menu
        const facilityReportsIndex = facilityMenu.findIndex((item) => item.name === 'reports');

        if (facilityReportsIndex !== -1) {
            facilityMenu.splice(facilityReportsIndex, 1);
        }

        oldUIMenu.push({
            id: 'pages.help.TYPE',
            ref: helpRef,
            name: 'help',
            reference: 'help',
            icon: 'info',
            tooltipText: 'pages.help.Type'
        });
    }

    function hasPermission(item, authUser) {
        if (!item.permission) return true;
        return item.permission.some((p) => authUser.permissions?.includes(p));
    }

    function hasSubPermission(item, authUser) {
        if (!item.subPermission) return true;
        return !authUser.permissions?.includes(item.subPermission);
    }

    function hasFeature(item, authUser) {
        if (!item.feature) return true;
        return authUser.featureflagAssignments?.includes(item.feature);
    }

    function hasSubFeature(item, authUser) {
        if (!item.subFeature) return true;
        return !authUser.featureflagAssignments?.includes(item.subFeature);
    }

    function hasKeypadAccess(item, myFacility) {
        if (!item.keypad) return true;
        return myFacility?.has3K || myFacility?.has4K;
    }
    const showV3 = (item) => {
        if (item.name === 'settings' && authUser.featureflagAssignments.includes('new_ui_v3')) return false;
        if (item.name === 'network/all' && authUser.featureflagAssignments.includes('new_ui_v3')) return false;
        if (item.name === 'accessCodes/all' && authUser.featureflagAssignments.includes('new_ui_v3')) return false;
        if (item.name === 'hours' && authUser.featureflagAssignments.includes('new_ui_v3')) return false;
        if (item.name === 'maps/all' && authUser.featureflagAssignments.includes('new_ui_v3')) return false;
        return true;
    };

    function getFilteredMenu(menu, authUser, myFacility) {
        return menu.filter(
            (item) =>
                hasPermission(item, authUser) &&
                hasSubPermission(item, authUser) &&
                hasFeature(item, authUser) &&
                hasSubFeature(item, authUser) &&
                hasKeypadAccess(item, myFacility) &&
                showV3(item)
        );
    }

    const handleClick = (name) => {
        if (name === 'reports') {
            history.push('/app/reports');
            return;
        }

        if (myFacility) {
            history.push(`/app/facilities/facility/${myFacility.id}/${name}`);
        } else {
            history.push(`/app/facilities/facility/${authUser.defaultSiteId}/${name}`);
        }
    };

    const handleNotificationButton = () => {
        const placement = 'right';
        const rect = buttonRef.current.getBoundingClientRect();
        const padding = 5;

        let arrowOffset = 20; // default
        const viewportHeight = window.innerHeight;
        const modalWidth = (30 * viewportHeight) / 100;
        const modalHeight = 568;
        let x = 0,
            y = 0;
        if (placement === 'right') {
            x = rect.left + rect.width - padding;
            y = rect.top + rect.height / 2 - modalHeight / 2;
            if (y + modalHeight + 12 > viewportHeight) {
                y = viewportHeight - modalHeight - 100; // 24px buffer from bottom
            }
            arrowOffset = rect.top + rect.height / 2 - y - 25;
        } else if (placement === 'left') {
            x = rect.left - modalWidth - padding;
            y = rect.top + rect.height / 2 - modalHeight / 2;
            arrowOffset = rect.top + rect.height / 2 - y;
        } else {
            x = rect.left + rect.width / 2 - modalWidth / 2;
            y = rect.bottom + padding;
            arrowOffset = rect.left + rect.width / 2 - x;
        }

        setModalPosition({ x, y, arrowOffset });
        setPlacement(placement);
        setVisible(true);
    };

    // useEffect(() => {
    //   setCount(notificationcount);
    // },[notificationcount]);

    useEffect(() => {
        fetchAllNotifications();
    }, []);

    useEffect(() => {
        setCount(notificationsCount);
    }, [notificationsCount]);

    useEffect(() => {
        if (notificationsCompany && notificationsCompany.length > 0 && notificationsCompany[0]) {
            if (firstLoad && buttonRef.current) {
                // const timer = setTimeout(() => {
                //     buttonRef.current.click();
                // }, 1000); // Delay of 1000ms (1 second)
                setFirstLoad(false);

                setCount(notificationsCompany.length);
                dispatch(setNotificationsCount(notificationsCompany.length));
            }
        }
    }, [notificationsCompany, firstLoad]);

    const fetchAllNotifications = () => {
        const siteUUIDs = [];
        filteredSites.forEach((site) => {
            if (site?.uuid) {
                siteUUIDs.push(String(site.id));
            }
        });
        dispatch(
            fetchUserNotificationsCompany({
                page: 1,
                size: 1000,
                siteUUIDs: siteUUIDs,
                enterpriseNotifications: true
            })
        );
    };

    const filteredMenu = useMemo(() => {
        if (!myFacility) {
            return getFilteredMenu(oldUIMenu, authUser, site);
        } else {
            return getFilteredMenu(oldUIMenu, authUser, myFacility);
        }
    }, [authUser, myFacility, site]);

    return (
        <aside className="app-new-sidebar h-screen min-w-[114px] flex flex-col justify-between bg-[var(--color-glass-dark)] p-6">
            <div className="flex items-center h-[66px]">
                {theme?.menuLogo ? <img width="66" src={theme.menuLogo} alt="logo" /> : <Noke />}
            </div>
            <div className="flex-1 flex flex-col custom-scrollbar items-center overflow-y-auto py-2">
                {isFacilityView && (
                    <nav className="flex flex-col gap-2 pb-2 mt-auto">
                        {facilityMenu.map((item) => {
                            if (
                                (item.name === 'settings' || item.name === 'unitsusers') &&
                                !authUser.featureflagAssignments.includes('new_ui_v3')
                            ) {
                                return null;
                            }
                            return (
                                <div key={item.name}>
                                    <button
                                        type="button"
                                        ref={item.ref}
                                        id={`${item.name}-sidebar-button`}
                                        data-cy={`${item.name}-sidebar-button`}
                                        className="flex items-center justify-center"
                                        onClick={() => {
                                            handleClick(item.name);
                                        }}>
                                        <div
                                            className={`${isNewUIActive(item) ? 'bg-white' : 'bg-[var(--color-glass-x-light)]'} p-4 rounded-full`}>
                                            {item.icon}
                                        </div>
                                    </button>
                                    <Tooltip position="right" anchorRef={item.ref} text={item.tooltipText} />
                                </div>
                            );
                        })}
                    </nav>
                )}
                {isFacilityView && (
                    <div className="flex flex-col gap-2 mb-auto">
                        {filteredMenu.map((item) => (
                            <div key={item.name}>
                                <button
                                    type="button"
                                    ref={item.ref}
                                    id={`${item.name}-sidebar-button`}
                                    data-cy={`${item.name}-sidebar-button`}
                                    className="flex items-center justify-center"
                                    onClick={() => {
                                        dispatch(setIsNewUI(true));
                                        history.push(`/app/${item.name}`);
                                    }}>
                                    <div
                                        className={`${isOldUIActive(item) ? 'bg-white' : 'bg-[var(--color-glass-x-light)]'} p-4 rounded-full`}>
                                        <Icon name={item.icon} size={20} />
                                    </div>
                                </button>
                                <Tooltip
                                    position="right"
                                    anchorRef={item.ref}
                                    text={intl.formatMessage({ id: item.tooltipText })}
                                />
                            </div>
                        ))}
                    </div>
                )}
                <Popout
                    isOpen={isHelpPopoutOpen}
                    onClose={() => {
                        setIsHelpPopoutOpen(false);
                        setShowGeneralHelpMenu(false);
                    }}
                    anchorRef={helpPopoutAnchorRef}
                    positioning="right"
                    title={
                        !showGeneralHelpMenu
                            ? intl.formatMessage({ id: 'help.supportCenter' })
                            : intl.formatMessage({ id: 'help.generalHelp' })
                    }>
                    <div className="w-120 flex flex-col gap-8 mt-4">
                        {!showGeneralHelpMenu ? (
                            <>
                                <button
                                    type="button"
                                    id="sidebar-live-chat-button"
                                    data-cy="sidebar-live-chat-button"
                                    className="flex items-center"
                                    onClick={() => {
                                        window.HubSpotConversations.widget.open();
                                        setIsHelpPopoutOpen(false);
                                    }}>
                                    <IconWithBackground
                                        iconName="chat"
                                        backgroundColor="#F5F5F5"
                                        className="text-[var(--color-gray-300)]"
                                    />
                                    <h6 className="tw-heading-6 pl-4">{intl.formatMessage({ id: 'help.liveChat' })}</h6>
                                </button>
                                <button
                                    type="button"
                                    id="sidebar-general-help-button"
                                    data-cy="sidebar-general-help-button"
                                    className="flex items-center justify-between w-full"
                                    onClick={() => setShowGeneralHelpMenu(true)}>
                                    <div className="flex items-center">
                                        <IconWithBackground
                                            iconName="info"
                                            backgroundColor="#F5F5F5"
                                            className="text-[var(--color-gray-300)]"
                                        />
                                        <h6 className="tw-heading-6 pl-4">
                                            {intl.formatMessage({ id: 'help.generalHelp' })}
                                        </h6>
                                    </div>
                                    <Icon name="chevronRight" size={20} className="text-[var(--color-gray-400)]" />
                                </button>
                                <button
                                    type="button"
                                    id="sidebar-knowledgebase-button"
                                    data-cy="sidebar-knowledgebase-button"
                                    className="flex items-center"
                                    onClick={() => {
                                        window.open(
                                            'https://www.janusintl.com/knowledge',
                                            '_blank',
                                            'noopener,noreferrer'
                                        );
                                        setIsHelpPopoutOpen(false);
                                    }}>
                                    <IconWithBackground
                                        iconName="openBook"
                                        backgroundColor="#F5F5F5"
                                        className="text-[var(--color-gray-300)]"
                                    />
                                    <h6 className="tw-heading-6 pl-4">
                                        {intl.formatMessage({ id: 'form.login.knowledgeBase' })}
                                    </h6>
                                </button>
                                <button
                                    type="button"
                                    id="sidebar-resource-center-button"
                                    data-cy="sidebar-resource-center-button"
                                    className="flex items-center"
                                    onClick={() => {
                                        window.pendo.BuildingBlocks.BuildingBlockResourceCenter.getResourceCenter().show();
                                        setIsHelpPopoutOpen(false);
                                    }}>
                                    <IconWithBackground
                                        iconName="openBook"
                                        backgroundColor="#F5F5F5"
                                        className="text-[var(--color-gray-300)]"
                                    />
                                    <h6 className="tw-heading-6 pl-4">
                                        {intl.formatMessage({ id: 'help.resourceCenter' })}
                                    </h6>
                                </button>
                            </>
                        ) : (
                            <>
                                {/* General Help submenu options */}
                                <button
                                    type="button"
                                    id="sidebar-contact-us-button"
                                    data-cy="sidebar-contact-us-button"
                                    className="flex items-center"
                                    onClick={() => {
                                        history.push('/app/generalHelp/contact');
                                        setIsHelpPopoutOpen(false);
                                        setShowGeneralHelpMenu(false);
                                    }}>
                                    <IconWithBackground
                                        iconName="mail"
                                        backgroundColor="#F5F5F5"
                                        className="text-[var(--color-gray-300)]"
                                    />
                                    <h6 className="tw-heading-6 pl-4">
                                        {intl.formatMessage({ id: 'pages.contact.header' })}
                                    </h6>
                                </button>

                                <button
                                    type="button"
                                    id="sidebar-suggest-improvement-button"
                                    data-cy="sidebar-suggest-improvement-button"
                                    className="flex items-center"
                                    onClick={() => {
                                        history.push('/app/generalHelp/improvement');
                                        setIsHelpPopoutOpen(false);
                                        setShowGeneralHelpMenu(false);
                                    }}>
                                    <IconWithBackground
                                        iconName="lightBulb"
                                        backgroundColor="#F5F5F5"
                                        className="text-[var(--color-gray-300)]"
                                    />
                                    <h6 className="tw-heading-6 pl-4">
                                        {intl.formatMessage({ id: 'pages.suggestImprovement.Type' })}
                                    </h6>
                                </button>

                                <button
                                    type="button"
                                    id="sidebar-order-fobs-button"
                                    data-cy="sidebar-order-fobs-button"
                                    className="flex items-center"
                                    onClick={() => {
                                        history.push('/app/generalHelp/order');
                                        setIsHelpPopoutOpen(false);
                                        setShowGeneralHelpMenu(false);
                                    }}>
                                    <IconWithBackground
                                        iconName="fob"
                                        backgroundColor="#F5F5F5"
                                        className="text-[var(--color-gray-300)]"
                                    />
                                    <h6 className="tw-heading-6 pl-4">
                                        {intl.formatMessage({ id: 'pages.orderFobs.header' })}
                                    </h6>
                                </button>
                            </>
                        )}
                    </div>
                </Popout>
                <Popout
                    isOpen={isUserPopoutOpen}
                    onClose={() => setIsUserPopoutOpen(false)}
                    anchorRef={userPopoutAnchorRef}
                    positioning="right"
                    header={false}>
                    <div className="w-60">
                        {authUser?.role?.tier > 1 && (
                            <button
                                type="button"
                                id="sidebar-manage-profile-button"
                                data-cy="sidebar-manage-profile-button"
                                onClick={() => {
                                    openModal(
                                        <UnitsUsersDetailModal
                                            trigger={`${authUser.uuid}-${Date.now()}`}
                                            unitUser={{ User: { id: authUser.id, uuid: authUser.uuid } }}
                                        />
                                    );
                                    setIsUserPopoutOpen(false);
                                }}
                                className="tw-paragraph-reg block text-left px-4 py-2 mt-6 hover:bg-[var(--color-gray-100)] rounded-[8px]">
                                {intl.formatMessage({ id: 'user.manageProfile' })}
                            </button>
                        )}
                        <button
                            type="button"
                            id="sidebar-logout-button"
                            data-cy="sidebar-logout-button"
                            onClick={() => dispatch(userSignOut())}
                            // take out the mt-6 below when you add the above button back in
                            className="tw-paragraph-reg block text-left mt-6 px-4 py-2 hover:bg-[var(--color-gray-100)] rounded-[8px]">
                            {intl.formatMessage({ id: 'comp.button.logout' })}
                        </button>
                    </div>
                </Popout>
            </div>
            <div className="flex flex-col items-center gap-2">
                {/* Notification Button and Badge */}
                {authUser?.featureflagAssignments?.includes('new_ui_v2') &&
                    authUser &&
                    authUser.role &&
                    authUser.role.tier !== 1 && (
                        <>
                            <div className="relative flex flex-col items-center">
                                <Button
                                    ref={buttonRef}
                                    icon="notification"
                                    type="uncontained"
                                    id="sidebar-notification-button"
                                    data-cy="sidebar-notification-button"
                                    size={20}
                                    onClick={handleNotificationButton}
                                />
                                {/* Notification badge */}
                                {count > 0 && (
                                    <span
                                        className="absolute -top-1 -right-0.5 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] flex items-center justify-center"
                                        style={{ fontSize: '10px', lineHeight: '1' }}>
                                        {count}
                                    </span>
                                )}
                            </div>
                            <NotificationModal
                                isVisible={visible}
                                ref={buttonRef}
                                onClose={() => setVisible(false)}
                                position={modalPosition}
                                placement={placement}
                            />
                        </>
                    )}
                <Button
                    ref={helpPopoutAnchorRef}
                    icon="help"
                    type="uncontained"
                    id="sidebar-help-button"
                    data-cy="sidebar-help-button"
                    onClick={() => setIsHelpPopoutOpen(true)}
                />
                <Tooltip
                    position="right"
                    anchorRef={helpPopoutAnchorRef}
                    text={intl.formatMessage({ id: 'help.supportCenter' })}
                />
            </div>
            <div className="pt-2 pb-4">
                <LanguageSwitcher noIcon={true} />
            </div>
            <div className="h-[48px] flex justify-center" ref={userPopoutAnchorRef}>
                <UserInitialsAvatar
                    id="sidebar-user-button"
                    data-cy="sidebar-user-button"
                    firstName={authUser.firstName}
                    lastName={authUser.lastName}
                    onClick={() => setIsUserPopoutOpen(true)}
                />
            </div>
        </aside>
    );
};

export default Sidebar;
